import React, { useState } from 'react';
import styled from 'styled-components';
import { Icon } from 'components/Icon';
import { Text } from 'components/Typography';
import { theme, toRem } from 'design';
import { ITipsBreakdownLineItem } from 'screens/MakePayment/Calculator/types';
import { centsToDollarsDisplay } from 'utils/currency';

interface ILineItemProps {
  item: ITipsBreakdownLineItem;
  dataCy?: string;
  className?: string;
}

const LabelContainer = styled.div`
  display: flex;
  align-items: center;
  margin-right: ${toRem(theme.space.xl)};
`;

const Label = styled.p``;

const Description = styled.span`
  color: ${theme.color.neutral[500]};
`;

const DropdownButton = styled.button.attrs({
  type: 'button',
})`
  display: flex;
  flex-grow: 0;
  flex-shrink: 0;
  margin-right: ${toRem(theme.space['2xs'])};
  transform: rotate(270deg);

  &.open {
    transform: none;
  }
`;

const Item = styled.div`
  display: grid;
  grid-template-columns: auto auto;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  font-size: ${toRem(theme.font.size.xs)};
  color: ${theme.color.neutral[900]};

  &:not(:last-child) {
    margin-bottom: ${toRem(theme.space.sm)};
  }

  &.sub-item {
    margin-bottom: 0;
  }
`;

const Details = styled.div`
  display: flex;
  flex-flow: column nowrap;
  gap: ${toRem(theme.space.sm)};
  margin: 0 0 ${toRem(theme.space.sm)} ${toRem(theme.space.lg)};

  p {
    font-style: italic;
    color: ${theme.color.neutral[500]};
  }
`;

const LineItem: React.FC<ILineItemProps> = ({ item, dataCy, className }) => {
  const [open, setOpen] = useState(false);

  return (
    <>
      <Item data-cy={dataCy} className={className}>
        <LabelContainer>
          {item.subItems?.length > 0 && (
            <DropdownButton aria-label="View Categories" data-cy="view-categories-btn" onClick={() => setOpen(!open)} className={open ? 'open' : ''}>
              <Icon icon="Dropdown" fill={theme.color.neutral[600]} width={14} height={12} />
            </DropdownButton>
          )}
          <Label>
            {item.label}
            {item.description && <Description>&nbsp;{item.description}</Description>}
          </Label>
        </LabelContainer>
        <Text size="xsmall">{centsToDollarsDisplay(item.amount)}</Text>
      </Item>
      {open && (
        <Details>
          {item.subItems?.map((item) => (
            <Item key={`sub_item_${item.label}`} className="sub-item">
              <Label>{item.label}</Label>
              <Text size="xsmall">{centsToDollarsDisplay(item.amount)}</Text>
            </Item>
          ))}
        </Details>
      )}
    </>
  );
};

export default LineItem;
